import React, { useEffect, useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { HiCheckCircle, HiPaperClip, HiPlus, HiXMark } from 'react-icons/hi2';
import { toast } from 'react-toastify';
import { createSubmission, uploadAttachment } from '../../services/productDevelopmentService';
import { STAGE_FIELDS, STAGE_NAMES } from './StageSubmissionForm';

const StaffSubmissionFormModal = ({ isOpen, onClose, onSuccess, clients = [], currentUser }) => {
  const [selectedClient, setSelectedClient] = useState('');
  const [selectedStage, setSelectedStage] = useState('idea');
  const [formData, setFormData] = useState({});
  const [attachments, setAttachments] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!isOpen) {
      setSelectedClient('');
      setSelectedStage('idea');
      setFormData({});
      setAttachments([]);
      return;
    }
    setFormData({});
  }, [isOpen]);

  const activeFields = useMemo(() => {
    return STAGE_FIELDS[selectedStage] || [];
  }, [selectedStage]);

  if (!isOpen) {
    return null;
  }

  const handleChange = event => {
    const { name, value } = event.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleAttachmentSelection = async event => {
    const files = Array.from(event.target.files || []);
    if (files.length === 0) return;

    setUploading(true);
    const prepared = files.map(file => ({
      name: file.name,
      size: file.size,
      type: file.type,
      file,
      id: `${file.name}_${Date.now()}`,
    }));

    setAttachments(prev => [...prev, ...prepared]);
    setUploading(false);
    event.target.value = '';
  };

  const removeAttachment = id => {
    setAttachments(prev => prev.filter(item => item.id !== id));
  };

  const handleSubmit = async event => {
    event.preventDefault();

    if (!selectedClient) {
      toast.error('Select a client for this submission');
      return;
    }

    const clientRecord = clients.find(client => client.uid === selectedClient);
    if (!clientRecord?.email) {
      toast.error('Unable to determine client email address');
      return;
    }

    const requiredFields = activeFields.filter(field => field.required);
    const missing = requiredFields.filter(field => !formData[field.id]?.trim());
    if (missing.length > 0) {
      toast.error(`Provide values for: ${missing.map(field => field.label).join(', ')}`);
      return;
    }

    setSaving(true);
    try {
      const uploadedAttachments = [];
      for (const attachment of attachments) {
        try {
          const uploaded = await uploadAttachment(
            attachment.file,
            clientRecord.uid,
            `staff_${Date.now()}`
          );
          uploadedAttachments.push(uploaded);
        } catch (error) {
          toast.error(`Failed to upload ${attachment.name}`);
        }
      }

      await createSubmission(
        {
          stage: selectedStage,
          stageName: STAGE_NAMES[selectedStage],
          ...formData,
          attachments: uploadedAttachments,
          createdByStaffId: currentUser?.uid || null,
          createdByStaffEmail: currentUser?.email || null,
          createdByStaffName: currentUser?.displayName || currentUser?.name || null,
        },
        clientRecord.uid,
        clientRecord.email
      );

      toast.success('Submission recorded for client');
      onSuccess?.();
      onClose();
    } catch (error) {
      toast.error('Failed to create submission');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.95 }}
        className="max-h-[90vh] w-full max-w-4xl overflow-y-auto rounded-lg bg-white shadow-xl"
      >
        <div className="sticky top-0 flex items-center justify-between border-b border-gray-200 bg-white px-6 py-4">
          <div>
            <h2 className="text-xl font-semibold text-black">Log Submission for Client</h2>
            <p className="text-sm text-gray-600">
              Create a submission on behalf of a client for tracking and support.
            </p>
          </div>
          <button
            onClick={onClose}
            className="rounded-lg p-2 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600"
          >
            <HiXMark className="h-5 w-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6 p-6">
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <div>
              <label className="mb-2 block text-sm font-medium text-gray-700">Select Client</label>
              <select
                value={selectedClient}
                onChange={event => setSelectedClient(event.target.value)}
                className="w-full rounded-lg border border-gray-300 px-4 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                required
              >
                <option value="">Choose client...</option>
                {clients.map(client => (
                  <option key={client.uid || client.email} value={client.uid}>
                    {client.name || client.email} ({client.email})
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="mb-2 block text-sm font-medium text-gray-700">Stage</label>
              <select
                value={selectedStage}
                onChange={event => setSelectedStage(event.target.value)}
                className="w-full rounded-lg border border-gray-300 px-4 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20"
              >
                {Object.entries(STAGE_NAMES).map(([key, value]) => (
                  <option key={key} value={key}>
                    {value}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="space-y-6">
            {activeFields.map(field => (
              <div key={field.id}>
                <label className="mb-2 block text-sm font-medium text-gray-700">
                  {field.label} {field.required && <span className="text-red-500">*</span>}
                </label>
                {field.type === 'textarea' ? (
                  <textarea
                    name={field.id}
                    value={formData[field.id] || ''}
                    onChange={handleChange}
                    required={field.required}
                    rows={4}
                    className="w-full rounded-lg border border-gray-300 px-4 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                    placeholder={`Enter ${field.label.toLowerCase()}...`}
                  />
                ) : (
                  <input
                    type={field.type || 'text'}
                    name={field.id}
                    value={formData[field.id] || ''}
                    onChange={handleChange}
                    required={field.required}
                    className="w-full rounded-lg border border-gray-300 px-4 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                    placeholder={`Enter ${field.label.toLowerCase()}...`}
                  />
                )}
              </div>
            ))}
          </div>
          <div>
            <label className="mb-2 block text-sm font-medium text-gray-700">Attachments</label>
            <div className="rounded-lg border-2 border-dashed border-gray-300 p-4">
              <input
                id="staff-submission-attachments"
                type="file"
                multiple
                className="hidden"
                onChange={handleAttachmentSelection}
                disabled={uploading}
              />
              <label
                htmlFor="staff-submission-attachments"
                className="flex cursor-pointer flex-col items-center justify-center gap-2"
              >
                <HiPaperClip className="h-8 w-8 text-gray-400" />
                <span className="text-sm text-gray-600">
                  {uploading ? 'Preparing files...' : 'Click to upload files or drag and drop'}
                </span>
                <span className="text-xs text-gray-500">PDF, DOC, XLS, images</span>
              </label>
            </div>
            {attachments.length > 0 && (
              <div className="mt-4 space-y-2">
                {attachments.map(item => (
                  <div
                    key={item.id}
                    className="flex items-center justify-between rounded-lg border border-gray-200 bg-gray-50 p-3"
                  >
                    <div className="flex items-center gap-3">
                      <HiPaperClip className="h-5 w-5 text-gray-400" />
                      <div>
                        <p className="text-sm font-medium text-gray-900">{item.name}</p>
                        <p className="text-xs text-gray-500">{(item.size / 1024).toFixed(2)} KB</p>
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => removeAttachment(item.id)}
                      className="rounded-lg p-1 text-red-600 transition-colors hover:bg-red-50"
                    >
                      <HiXMark className="h-4 w-4" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="flex justify-end gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className="flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-blue-700 disabled:opacity-50"
            >
              {saving ? (
                <>
                  <div className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
                  Saving...
                </>
              ) : (
                <>
                  <HiPlus className="h-5 w-5" />
                  Create Submission
                </>
              )}
            </button>
          </div>
        </form>
      </motion.div>
    </div>
  );
};

export default StaffSubmissionFormModal;
